/**
 * CarouselVideo Composition (Client-side)
 *
 * Sequences carousel slides into an animated video using TransitionSeries.
 * Each slide reuses RemotionSlide, with overlay animations driven by
 * the ChoreographySpec.
 *
 * This is the client-side mirror of server/src/remotion/CarouselVideo.tsx.
 * The client version does not include FontLoaderWrapper (fonts are loaded
 * via CSS on the client).
 */

import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { TransitionSeries, linearTiming, springTiming } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { flip } from '@remotion/transitions/flip';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { AnyOverlay, TextOverlay, ImageFilter } from '../../models/Post';
import {
  ChoreographySpec,
  SlideChoreography,
  SlideTransition,
  OverlayEntrance,
  AnimationTiming,
  SPRING_CONFIGS,
} from '../../models/ChoreographySpec';
import { RemotionSlide } from './RemotionSlide';
import { useVideoTextScale } from './useVideoTextScale';
import { adaptVideoOverlay } from './adaptVideoOverlay';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlideData {
  overlays: AnyOverlay[];
  backgroundImageUrl?: string;
  backgroundColor?: string;
  backgroundFilter?: ImageFilter;
  dimensions: { width: number; height: number };
  imageUrls?: Record<string, string>;
  isBackgroundTemplateAsset?: boolean;
}

export interface CarouselVideoProps {
  [key: string]: unknown;
  slides: SlideData[];
  choreography: ChoreographySpec;
  /** Output video width */
  width: number;
  /** Output video height */
  height: number;
  referenceWidth?: number;
  referenceHeight?: number;
  layoutEngine?: 'fabric' | 'remotion';
  /** When output aspect ratio differs from slide aspect ratio, adapt background layers to fill. */
  fillMode?: 'adaptive' | 'blur' | 'color' | 'none';
  /** Signed URL for background music track */
  musicUrl?: string;
  /** Music volume 0-1 */
  musicVolume?: number;
}

// ---------------------------------------------------------------------------
// Helpers: resolve timing to Remotion easing
// ---------------------------------------------------------------------------

function resolveEasing(timing?: AnimationTiming): ((t: number) => number) {
  if (!timing || timing.method === 'easing') {
    const curve = timing?.method === 'easing' ? timing.curve : 'easeOut';
    switch (curve) {
      case 'linear': return Easing.linear;
      case 'easeIn': return Easing.in(Easing.cubic);
      case 'easeOut': return Easing.out(Easing.cubic);
      case 'easeInOut': return Easing.inOut(Easing.cubic);
      case 'cubic': return Easing.cubic;
      case 'exp': return Easing.exp;
      case 'circle': return Easing.circle;
      default: return Easing.out(Easing.cubic);
    }
  }
  if (timing.method === 'bezier') {
    return Easing.bezier(...timing.points);
  }
  return Easing.out(Easing.cubic);
}

// ---------------------------------------------------------------------------
// Helpers: collect full-page background overlays
// ---------------------------------------------------------------------------

/**
 * Returns large non-text background overlays. These remain distinct
 * editable layers in the post; at render time they travel with the background
 * so the adapted video does not create a centered card.
 */
function isBackgroundLayerOverlay(overlay: AnyOverlay): boolean {
  if (overlay.type === 'text' || overlay.type === undefined) return false;
  const w = overlay.width ?? 0;
  const h = overlay.height ?? 0;
  const x = overlay.position?.x ?? 0;
  const y = overlay.position?.y ?? 0;
  const extendsRight = x + w >= 97;
  const extendsBottom = y + h >= 97;
  const spansWidth = x <= 3 && w >= 95;
  const spansHeight = y <= 3 && h >= 95;
  const largeBackgroundPlate = spansWidth && y <= 12 && h >= 70;
  const fullCanvas = spansWidth && spansHeight;
  const horizontalRule = spansWidth && h <= 2;
  const oversizedSidePlate =
    (overlay.zIndex ?? 0) <= 1 &&
    w >= 55 &&
    h >= 90 &&
    y <= 3 &&
    extendsBottom &&
    (x <= 3 || extendsRight);
  return fullCanvas || largeBackgroundPlate || horizontalRule || oversizedSidePlate;
}

function getBackgroundLayerOverlays(overlays: AnyOverlay[]): AnyOverlay[] {
  return [...overlays]
    .filter(isBackgroundLayerOverlay)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
}

// ---------------------------------------------------------------------------
// Helpers: resolve slide transition presentation
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveTransitionPresentation(transition: SlideTransition, width: number, height: number): any {
  const dir = transition.direction ?? 'from-right';

  switch (transition.type) {
    case 'fade': return fade();
    case 'slide': return slide({ direction: dir });
    case 'wipe': return wipe({ direction: dir });
    case 'flip': return flip({ direction: dir === 'from-right' ? 'from-right' : 'from-left' });
    case 'clockWipe': return clockWipe({ width, height });
    default: return fade();
  }
}

function resolveTransitionTiming(transition: SlideTransition, fps: number) {
  const durationInFrames = Math.round(transition.durationSeconds * fps);
  if (transition.timing === 'spring') {
    return springTiming({ config: { damping: 200 }, durationInFrames });
  }
  return linearTiming({ durationInFrames });
}

// ---------------------------------------------------------------------------
// AnimatedSlide: wraps RemotionSlide with choreography-driven animations
// ---------------------------------------------------------------------------

/** The plan watermark (server and editor ids) is not slide content: it never moves with entrances or transitions. */
const isWatermarkOverlay = (o: AnyOverlay) =>
  o.id === 'wavegen-watermark' || o.id === '__watermark__' || (o.type === 'image' && (o as { filePath?: string }).filePath === 'wavegen-watermark.png');
const withoutWatermark = (sd: SlideData): SlideData =>
  sd.overlays.some(isWatermarkOverlay) ? { ...sd, overlays: sd.overlays.filter((o) => !isWatermarkOverlay(o)) } : sd;
/** Only the watermark, on a transparent slide, so it lands exactly where the slide would have put it. */
const watermarkOnly = (sd: SlideData): SlideData => ({
  ...sd,
  overlays: sd.overlays.filter(isWatermarkOverlay),
  backgroundImageUrl: undefined,
  backgroundColor: 'transparent',
  backgroundFilter: undefined,
});
const STILL_SLIDE: SlideChoreography = { slideIndex: 0, durationSeconds: 1, overlayEntrances: [], transitionOut: { type: 'none', durationSeconds: 0 } };

interface AnimatedSlideProps {
  slideData: SlideData;
  slideChoreography: SlideChoreography;
  /** Output (video) width */
  outputWidth: number;
  /** Output (video) height */
  outputHeight: number;
  referenceWidth?: number;
  referenceHeight?: number;
  layoutEngine?: 'fabric' | 'remotion';
  fillMode?: 'adaptive' | 'blur' | 'color' | 'none';
}

const AnimatedSlide: React.FC<AnimatedSlideProps> = ({
  slideData,
  slideChoreography,
  outputWidth,
  outputHeight,
  referenceWidth,
  referenceHeight,
  layoutEngine,
  fillMode = 'adaptive',
}) => {
  const { fps } = useVideoConfig();

  const slideWidth = slideData.dimensions.width;
  const slideHeight = slideData.dimensions.height;
  const slideAspect = slideWidth / slideHeight;
  const outputAspect = outputWidth / outputHeight;
  const needsFill = Math.abs(slideAspect - outputAspect) >= 0.02 && fillMode !== 'none';

  // Adaptive fill uses the full canvas; other fill modes preserve a centered slide.
  let innerWidth = outputWidth;
  let innerHeight = outputHeight;
  let offsetX = 0;
  let offsetY = 0;
  if (needsFill && fillMode !== 'adaptive') {
    if (slideAspect > outputAspect) {
      innerWidth = outputWidth;
      innerHeight = outputWidth / slideAspect;
    } else {
      innerHeight = outputHeight;
      innerWidth = outputHeight * slideAspect;
    }
    offsetX = (outputWidth - innerWidth) / 2;
    offsetY = (outputHeight - innerHeight) / 2;
  }

  const contentWidth = needsFill ? innerWidth : outputWidth;
  const contentHeight = needsFill ? innerHeight : outputHeight;

  // Build a map of overlayId → entrance config
  const entranceMap = new Map<string, OverlayEntrance>();
  for (const entrance of slideChoreography.overlayEntrances) {
    entranceMap.set(entrance.overlayId, entrance);
  }

  const backgroundLayerOverlays = getBackgroundLayerOverlays(slideData.overlays);


  // ALL overlays go through AnimatedOverlayWrapper for consistent z-index handling.
  // RemotionSlide only renders the background (no overlays).
  const allOverlays = [...slideData.overlays]
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
    .map((overlay) => {
      const entrance = entranceMap.get(overlay.id) ?? {
        overlayId: overlay.id,
        type: 'none' as const,
        delaySeconds: 0,
        durationSeconds: 0,
      };
      return { overlay, entrance };
    });
  const useAdaptiveFill = needsFill && fillMode === 'adaptive';
  const useBlurFill = needsFill && fillMode === 'blur' && !!slideData.backgroundImageUrl;
  const foregroundOverlays = allOverlays;
  const contentOverlays = useAdaptiveFill
    ? foregroundOverlays.map(({ overlay, entrance }) => ({
        entrance,
        overlay: adaptVideoOverlay(
          overlay, slideWidth, slideHeight,
          outputWidth, outputHeight, referenceWidth ?? slideWidth, slideData.overlays, layoutEngine,
        ),
      }))
    : foregroundOverlays;

  const backgroundCoverScale = needsFill
    ? Math.max(outputWidth / slideWidth, outputHeight / slideHeight)
    : 1;
  const backgroundWidth = slideWidth * backgroundCoverScale;
  const backgroundHeight = slideHeight * backgroundCoverScale;
  const backgroundOffsetX = (outputWidth - backgroundWidth) / 2;
  const backgroundOffsetY = (outputHeight - backgroundHeight) / 2;

  return (
    <AbsoluteFill style={{ backgroundColor: slideData.backgroundColor ?? '#000' }}>
      {/* Adaptive fill: only the background photo is cover-cropped; overlays share the video canvas. */}
      {useAdaptiveFill && (
        <div
          style={{
            position: 'absolute',
            left: backgroundOffsetX,
            top: backgroundOffsetY,
            width: backgroundWidth,
            height: backgroundHeight,
            overflow: 'hidden',
          }}
        >
          <RemotionSlide
            width={backgroundWidth}
            height={backgroundHeight}
            backgroundColor={slideData.backgroundColor}
            backgroundImageUrl={slideData.backgroundImageUrl}
            backgroundFilter={slideData.backgroundFilter}
            overlays={[]}
            imageUrls={slideData.imageUrls}
            isVideo={false}
            fps={fps}
            referenceWidth={referenceWidth ?? slideWidth}
            referenceHeight={referenceHeight ?? slideHeight}
            layoutEngine={layoutEngine}
          />
        </div>
      )}

      {/* Legacy blur fill, kept available for comparison/explicit use. */}
      {useBlurFill && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            filter: 'blur(12px)',
            transform: 'scale(1.1)',
            overflow: 'hidden',
          }}
        >
          <RemotionSlide
            width={outputWidth}
            height={outputHeight}
            backgroundColor={slideData.backgroundColor}
            backgroundImageUrl={slideData.backgroundImageUrl}
            backgroundFilter={slideData.backgroundFilter}
            overlays={backgroundLayerOverlays}
            imageUrls={slideData.imageUrls}
            isVideo={false}
            fps={fps}
            referenceWidth={referenceWidth ?? slideWidth}
            referenceHeight={referenceHeight ?? slideHeight}
            layoutEngine={layoutEngine}
          />
        </div>
      )}

      {/* Adaptive content uses percentages of the full video canvas. */}
      <div
        style={needsFill ? {
          position: 'absolute',
          left: offsetX,
          top: offsetY,
          width: innerWidth,
          height: innerHeight,
          overflow: 'hidden',
        } : {
          position: 'absolute',
          inset: 0,
        }}
      >
        {!useAdaptiveFill && (
          <RemotionSlide
            width={contentWidth}
            height={contentHeight}
            backgroundColor={slideData.backgroundColor}
            backgroundImageUrl={slideData.backgroundImageUrl}
            backgroundFilter={slideData.backgroundFilter}
            overlays={[]}
            imageUrls={slideData.imageUrls}
            isVideo={false}
            fps={fps}
            referenceWidth={referenceWidth ?? slideWidth}
            referenceHeight={referenceHeight ?? slideHeight}
            layoutEngine={layoutEngine}
          />
        )}

        {/* All overlays with explicit z-index wrappers */}
        {contentOverlays.map(({ overlay, entrance }) => (
          <AnimatedOverlayWrapper
            key={overlay.id}
            overlay={overlay}
            adaptTextToHeight={useAdaptiveFill}
            sourceTextLayout={useAdaptiveFill ? { width: slideWidth, height: slideHeight, referenceWidth: referenceWidth ?? slideWidth } : undefined}
            entrance={entrance}
            containerWidth={contentWidth}
            containerHeight={contentHeight}
            fps={fps}
            referenceWidth={referenceWidth ?? slideWidth}
            referenceHeight={referenceHeight ?? slideHeight}
            layoutEngine={layoutEngine}
            imageUrls={slideData.imageUrls}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

// ---------------------------------------------------------------------------
// AnimatedOverlayWrapper: handles ALL overlay entrance animations with explicit z-index
// ---------------------------------------------------------------------------

interface AnimatedOverlayWrapperProps {
  adaptTextToHeight?: boolean;
  sourceTextLayout?: { width: number; height: number; referenceWidth: number };
  overlay: AnyOverlay;
  entrance: OverlayEntrance;
  containerWidth: number;
  containerHeight: number;
  fps: number;
  referenceWidth?: number;
  referenceHeight?: number;
  layoutEngine?: 'fabric' | 'remotion';
  imageUrls?: Record<string, string>;
}

export const AnimatedOverlayWrapper: React.FC<AnimatedOverlayWrapperProps> = ({
  overlay,
  entrance,
  containerWidth,
  containerHeight,
  fps,
  referenceWidth,
  referenceHeight,
  layoutEngine,
  imageUrls,
  adaptTextToHeight = false,
  sourceTextLayout,
}) => {
  const textScale = useVideoTextScale(
    overlay, containerWidth, containerHeight,
    adaptTextToHeight ? containerHeight / (referenceHeight || containerHeight) : undefined,
    layoutEngine, sourceTextLayout,
  );
  // RemotionSlide scales text from referenceWidth; only text gets the height scale.
  const effectiveReferenceWidth = textScale ? containerWidth / textScale : referenceWidth;
  const frame = useCurrentFrame();
  const delayFrames = Math.round(entrance.delaySeconds * fps);
  const durationFrames = Math.max(1, Math.round(entrance.durationSeconds * fps));
  const localFrame = frame - delayFrames;

  if (localFrame < 0) return null;

  const easing = resolveEasing(entrance.timing);
  const isSpring = entrance.timing?.method === 'spring';
  const springConfig = isSpring && entrance.timing?.method === 'spring'
    ? SPRING_CONFIGS[entrance.timing.config]
    : undefined;

  let animationStyle: React.CSSProperties = {};

  switch (entrance.type) {
    case 'none': {
      // No animation — fully visible
      break;
    }
    case 'fadeIn': {
      const progress = isSpring
        ? spring({ frame: localFrame, fps, config: springConfig ?? SPRING_CONFIGS.smooth, durationInFrames: durationFrames })
        : interpolate(localFrame, [0, durationFrames], [0, 1], {
            easing,
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
      animationStyle = { opacity: progress };
      break;
    }
    case 'slideIn': {
      const dir = entrance.direction ?? 'bottom';
      const progress = isSpring
        ? spring({ frame: localFrame, fps, config: springConfig ?? SPRING_CONFIGS.smooth, durationInFrames: durationFrames })
        : interpolate(localFrame, [0, durationFrames], [0, 1], {
            easing,
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
      const distance = 120; // percentage offset for offscreen start
      const translateMap: Record<string, string> = {
        left: `translateX(${(1 - progress) * -distance}%)`,
        right: `translateX(${(1 - progress) * distance}%)`,
        top: `translateY(${(1 - progress) * -distance}%)`,
        bottom: `translateY(${(1 - progress) * distance}%)`,
      };
      animationStyle = { transform: translateMap[dir] ?? translateMap.bottom };
      break;
    }
    case 'spring': {
      const progress = spring({
        frame: localFrame,
        fps,
        config: springConfig ?? SPRING_CONFIGS.bouncy,
        durationInFrames: durationFrames,
      });
      animationStyle = { transform: `scale(${progress})`, opacity: progress };
      break;
    }
    case 'scaleIn': {
      const progress = isSpring
        ? spring({ frame: localFrame, fps, config: springConfig ?? SPRING_CONFIGS.smooth, durationInFrames: durationFrames })
        : interpolate(localFrame, [0, durationFrames], [0, 1], {
            easing,
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
      animationStyle = { transform: `scale(${progress})`, opacity: progress };
      break;
    }
    case 'typewriter': {
      const progress = interpolate(localFrame, [0, durationFrames], [0, 1], {
        easing: Easing.linear,
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
      animationStyle = { __typewriterProgress: progress } as any;
      break;
    }
    case 'wipe': {
      const dir = entrance.direction ?? 'left';
      const progress = isSpring
        ? spring({ frame: localFrame, fps, config: springConfig ?? SPRING_CONFIGS.smooth, durationInFrames: durationFrames })
        : interpolate(localFrame, [0, durationFrames], [0, 1], {
            easing,
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
      const insetMap: Record<string, string> = {
        left: `inset(0 ${(1 - progress) * 100}% 0 0)`,
        right: `inset(0 0 0 ${(1 - progress) * 100}%)`,
        top: `inset(0 0 ${(1 - progress) * 100}% 0)`,
        bottom: `inset(${(1 - progress) * 100}% 0 0 0)`,
      };
      animationStyle = { clipPath: insetMap[dir] ?? insetMap.left };
      break;
    }
    case 'blur': {
      const progress = isSpring
        ? spring({ frame: localFrame, fps, config: springConfig ?? SPRING_CONFIGS.smooth, durationInFrames: durationFrames })
        : interpolate(localFrame, [0, durationFrames], [0, 1], {
            easing,
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
      const blurPx = interpolate(progress, [0, 1], [20, 0]);
      animationStyle = {
        filter: `blur(${blurPx}px)`,
        opacity: progress,
      };
      break;
    }
  }

  // Typewriter: modify text content
  if (entrance.type === 'typewriter' && overlay.type === 'text') {
    const progress = (animationStyle as any).__typewriterProgress ?? 1;
    const fullText = (overlay as TextOverlay).text;
    const chars = Math.round(progress * fullText.length);
    const typewriterOverlay: AnyOverlay = {
      ...overlay,
      text: fullText.substring(0, chars),
    } as TextOverlay;

    return (
      <div style={{ position: 'absolute', inset: 0, zIndex: overlay.zIndex ?? 0 }}>
        <RemotionSlide
          width={containerWidth}
          height={containerHeight}
          backgroundColor="transparent"
          overlays={[typewriterOverlay]}
          imageUrls={imageUrls}
          isVideo={true}
          videoDuration={99}
          fps={fps}
          referenceWidth={effectiveReferenceWidth}
          referenceHeight={referenceHeight}
          layoutEngine={layoutEngine}
        />
      </div>
    );
  }

  // Other custom types: wrap in animation div, preserving zIndex
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: overlay.zIndex ?? 0, ...animationStyle }}>
      <RemotionSlide
        width={containerWidth}
        height={containerHeight}
        backgroundColor="transparent"
        overlays={[overlay]}
        imageUrls={imageUrls}
        isVideo={true}
        videoDuration={99}
        fps={fps}
        referenceWidth={effectiveReferenceWidth}
        referenceHeight={referenceHeight}
        layoutEngine={layoutEngine}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// CarouselVideo: main composition
// ---------------------------------------------------------------------------

export const CarouselVideo: React.FC<CarouselVideoProps> = ({
  slides,
  choreography,
  width,
  height,
  referenceWidth,
  referenceHeight,
  layoutEngine,
  fillMode = 'adaptive',
  musicUrl,
  musicVolume = 0.7,
}) => {
  const { fps, durationInFrames } = useVideoConfig();
  const watermarkSlide = slides.find((s) => s.overlays.some(isWatermarkOverlay));

  const fadeInFrames = Math.round(1.5 * fps);  // 1.5s fade in
  const fadeOutFrames = Math.round(2 * fps);    // 2s fade out

  const musicVolumeCallback = React.useCallback(
    (f: number) => {
      const fadeIn = interpolate(f, [0, fadeInFrames], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
      const fadeOut = interpolate(
        f,
        [durationInFrames - fadeOutFrames, durationInFrames],
        [1, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      return fadeIn * fadeOut * musicVolume;
    },
    [fadeInFrames, fadeOutFrames, durationInFrames, musicVolume]
  );

  return (
    <>
    {musicUrl && (
      <Audio src={musicUrl} volume={musicVolumeCallback} loop />
    )}
    <TransitionSeries>
      {choreography.slides.map((slideChoreography, i) => {
        const slideData = slides[slideChoreography.slideIndex] ?? slides[i];
        if (!slideData) return null;

        const durationInFrames = Math.round(slideChoreography.durationSeconds * fps);
        const isLast = i === choreography.slides.length - 1;
        const transition = slideChoreography.transitionOut;

        return (
          <React.Fragment key={i}>
            <TransitionSeries.Sequence durationInFrames={durationInFrames}>
              <AnimatedSlide
                slideData={withoutWatermark(slideData)}
                slideChoreography={slideChoreography}
                outputWidth={width}
                outputHeight={height}
                referenceWidth={referenceWidth}
                referenceHeight={referenceHeight}
                layoutEngine={layoutEngine}
                fillMode={fillMode as 'adaptive' | 'blur' | 'color' | 'none'}
              />
            </TransitionSeries.Sequence>

            {!isLast && transition.type !== 'none' && (
              <TransitionSeries.Transition
                presentation={resolveTransitionPresentation(transition, width, height)}
                timing={resolveTransitionTiming(transition, fps)}
              />
            )}
          </React.Fragment>
        );
      })}
    </TransitionSeries>
    {watermarkSlide && (
      <AbsoluteFill>
        <AnimatedSlide
          slideData={watermarkOnly(watermarkSlide)}
          slideChoreography={STILL_SLIDE}
          outputWidth={width}
          outputHeight={height}
          referenceWidth={referenceWidth}
          referenceHeight={referenceHeight}
          layoutEngine={layoutEngine}
          fillMode={fillMode as 'adaptive' | 'blur' | 'color' | 'none'}
        />
      </AbsoluteFill>
    )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Calculate total duration from a choreography spec
// ---------------------------------------------------------------------------

export function calculateCarouselVideoDuration(
  choreography: ChoreographySpec,
): number {
  const fps = choreography.fps || 30;
  let totalFrames = 0;

  for (let i = 0; i < choreography.slides.length; i++) {
    const s = choreography.slides[i];
    totalFrames += Math.round(s.durationSeconds * fps);

    const isLast = i === choreography.slides.length - 1;
    if (!isLast && s.transitionOut.type !== 'none') {
      totalFrames -= Math.round(s.transitionOut.durationSeconds * fps);
    }
  }

  return Math.max(1, totalFrames);
}

export default CarouselVideo;
